import { ReactNode, useEffect, useRef } from 'react';
import { useWorkspaceStore } from '@shared/state';
import { Workspace } from '@shared/types';
import { getSyncEngine } from './engine';

function selectWorkspaceSnapshot(state: ReturnType<typeof useWorkspaceStore.getState>): Workspace {
  return {
    meta: state.meta,
    currentProjectId: state.currentProjectId,
    currentCalcMissionSessionId: state.currentCalcMissionSessionId,
    activeMissionId: state.activeMissionId,
    profile: state.profile,
    projects: state.projects,
    evaluations: state.evaluations,
    sipocs: state.sipocs,
    moscows: state.moscows,
    racis: state.racis,
    dealRooms: state.dealRooms,
    cras: state.cras,
    treasuries: state.treasuries,
    canvases: state.canvases,
    calcMissionSessions: state.calcMissionSessions,
    estimations: state.estimations,
    variables: state.variables,
    notes: state.notes,
    timeline: state.timeline,
    preferences: state.preferences,
  };
}

function syncToEngine(workspace: Workspace) {
  const engine = getSyncEngine();
  void engine.sync(workspace);
}

export function SyncProvider({ children }: { children: ReactNode }) {
  const initialized = useRef(false);

  const syncRef = useRef(syncToEngine);
  syncRef.current = syncToEngine;

  useEffect(() => {
    const engine = getSyncEngine();
    engine.initialize(selectWorkspaceSnapshot(useWorkspaceStore.getState()));
    initialized.current = true;

    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = useWorkspaceStore.subscribe((state, previousState) => {
      if (state.meta.updatedAt === previousState.meta.updatedAt) return;
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => syncRef.current(selectWorkspaceSnapshot(useWorkspaceStore.getState())), 800);
    });

    return unsubscribe;
  }, []);

  return <>{children}</>;
}

export function SyncInit() {
  useEffect(() => {
    const engine = getSyncEngine();
    const status = engine.getStatus();
    if (status.mode === 'cloud-ready') {
      const w = window as any;
      if (typeof w.IndxSync?.autoSync === 'function') {
        w.IndxSync.autoSync().catch(() => {});
      }
    }
  }, []);

  return null;
}
